import { useState } from "react";
import { useFileUrl } from "@/hooks/use-file-url";
import type { PublicAgentView } from "@/lib/public-agents";

/**
 * Given a resolved URL (or local SVG path), returns the light-mode and
 * dark-mode src values. Remote URLs use the same src for both; local SVG
 * paths derive `-dark` / `-light` variants.
 */
function resolveThemedSrcs(url: string): { light: string; dark: string } {
	const isAbsolute = url.startsWith("http://") || url.startsWith("https://");
	if (isAbsolute) return { light: url, dark: url };
	const base = url.replace(/\.svg$/, "");
	return { light: `${base}-dark.svg`, dark: `${base}-light.svg` };
}

/**
 * Build the `/api/logo` proxy URL that auto-pulls a favicon from the agent's
 * own website. `nofallback=1` makes the proxy 404 when no real logo is found so
 * the caller can render its own initials fallback instead of the proxy's plain
 * letter SVG.
 */
function faviconProxyUrl(website: string): string | null {
	try {
		const withProtocol = /^https?:\/\//i.test(website)
			? website
			: `https://${website}`;
		const host = new URL(withProtocol).hostname;
		if (!host) return null;
		return `/api/logo?host=${encodeURIComponent(host)}&nofallback=1`;
	} catch {
		return null;
	}
}

const SIZE_CONFIG = {
	sm: { wrapper: "size-8", img: "", text: "text-sm" },
	md: { wrapper: "size-12", img: "", text: "text-base" },
	lg: {
		wrapper: "size-16 sm:size-20",
		img: "",
		text: "text-base sm:text-lg",
	},
} as const;

export function AgentLogoImage({
	logoUrl,
	name,
	website,
	size = "md",
	mode,
}: {
	logoUrl?: string | null;
	name: string;
	website?: string | null;
	size?: keyof typeof SIZE_CONFIG;
	mode?: PublicAgentView["displayAvatarMode"];
}) {
	const cfg = SIZE_CONFIG[size];
	const { url: resolvedLogoUrl } = useFileUrl(logoUrl);
	const [faviconFailed, setFaviconFailed] = useState(false);

	const initials = (
		<div
			className={`inline-flex shrink-0 items-center justify-center overflow-hidden border border-border font-mono font-normal ${cfg.wrapper} ${cfg.text} ${
				mode === "stealth"
					? "bg-zinc-800 text-zinc-100"
					: "bg-muted text-foreground"
			}`}
		>
			{name}
		</div>
	);

	// Stealth never reveals a logo.
	if (mode === "stealth") return initials;

	// A stored (uploaded / seed) logo always wins over the auto-pulled favicon.
	if (resolvedLogoUrl) {
		const { light, dark } = resolveThemedSrcs(resolvedLogoUrl);
		const isAbsolute =
			resolvedLogoUrl.startsWith("http://") ||
			resolvedLogoUrl.startsWith("https://");

		return (
			<div
				className={`relative inline-flex shrink-0 items-center justify-center overflow-hidden ${isAbsolute ? "" : "dark:bg-background"} ${cfg.wrapper}`}
			>
				<img
					alt={name}
					className={`size-full object-contain ${cfg.img} block dark:hidden`}
					src={light}
					onError={(e) => {
						e.currentTarget.style.display = "none";
					}}
				/>
				<img
					alt={name}
					className={`size-full object-contain ${cfg.img} hidden dark:block`}
					src={dark}
					onError={(e) => {
						e.currentTarget.style.display = "none";
					}}
				/>
			</div>
		);
	}

	// No stored logo: auto-pull the favicon from the agent's website. Falls back
	// to initials if there's no website or the favicon can't be resolved.
	const faviconSrc = website ? faviconProxyUrl(website) : null;
	if (!faviconSrc || faviconFailed) return initials;

	return (
		<div
			className={`relative inline-flex shrink-0 items-center justify-center overflow-hidden border border-border bg-muted ${cfg.wrapper}`}
		>
			<img
				alt={name}
				className={`size-full object-contain ${cfg.img}`}
				src={faviconSrc}
				onError={() => setFaviconFailed(true)}
			/>
		</div>
	);
}
